import React, { useEffect, useState } from 'react';
import { Grid, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { Eye, Search, MapPin, Ruler, User, Phone, Calendar, IndianRupee, Tag, FileText, StickyNote, Navigation } from 'lucide-react';
import { AdminListingListItem } from '../../types/adminlistingsmodel';
import { getAllAdminListings } from '../../services/Adminlistings.api';
import { PageHeader } from '../../components/common/UIComponents';
import AdminListingDetailPopup from './Adminlistingdetail';

const inputSx = {
  '& .MuiOutlinedInput-root': { background: '#fff', borderRadius: '10px !important', fontSize: 13, fontFamily: 'DM Sans,sans-serif', '& fieldset': { borderColor: 'var(--border)' }, '&:hover fieldset': { borderColor: '#d4c4b0' }, '&.Mui-focused fieldset': { borderColor: 'var(--brand)' } },
  '& .MuiInputLabel-root': { fontSize: 13, fontFamily: 'DM Sans,sans-serif', color: 'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand)' },
};
const selectSx = { fontFamily: 'DM Sans,sans-serif', fontSize: 13, background: '#fff', borderRadius: '10px !important', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d4c4b0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--brand)' } };
const labelSx = { fontSize: 13, fontFamily: 'DM Sans,sans-serif', color: 'var(--text-muted)', '&.Mui-focused': { color: 'var(--brand)' } };

const statusColors: Record<string, { bg: string; color: string; border: string }> = {
  approved: { bg: 'var(--success-bg)', color: 'var(--success)', border: '#b8e8ce' },
  pending: { bg: 'var(--warning-bg)', color: 'var(--warning)', border: '#f3dfa8' },
  rejected: { bg: 'var(--danger-bg)', color: 'var(--danger)', border: '#f8c0c0' },
};

/* ---------- helpers ---------- */

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return `₹${value.toLocaleString('en-IN')}`;
};

const googleMapsUrl = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`;

/* ---------- small building blocks ---------- */

const DetailRow: React.FC<{ icon: React.ElementType; label: string; value: React.ReactNode }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-start gap-2">
    <Icon size={12} className="mt-[3px] shrink-0" color="var(--brand)" />
    <div className="min-w-0">
      <span className="block text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)', fontFamily: 'DM Sans,sans-serif' }}>
        {label}
      </span>
      <span className="block text-[12.5px] font-medium truncate" style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans,sans-serif' }}>
        {value ?? '—'}
      </span>
    </div>
  </div>
);

const AdminListings: React.FC = () => {
  const [items, setItems] = useState<AdminListingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getAllAdminListings()
      .then(setItems)
      .catch(err => setError(err.message || 'Failed to fetch listings.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(({ listing }) => {
    const status = (listing.approvalStatus || '').toLowerCase();
    if (filter !== 'all' && status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!listing.title.toLowerCase().includes(q) && !listing.city.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const openDetail = (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  return (
    <div className="min-h-screen px-8 py-7" style={{ background: 'var(--bg-base)' }}>
      <PageHeader
        title="Listing Management"
        subtitle="Review, approve and manage all ad space submissions"
        action={
          <div className="px-3.5 py-1.5 rounded-[10px] text-[13px] font-semibold" style={{ background: 'var(--brand-light)', border: '1px solid var(--brand-mid)', color: 'var(--brand)' }}>
            {filtered.length} listings
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 bg-white px-5 py-4 rounded-2xl shadow-sm" style={{ border: '1px solid var(--border)' }}>
        <TextField
          size="small"
          placeholder="Search by title or city..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, ...inputSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} color="var(--text-muted)" /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={labelSx}>Status</InputLabel>
          <Select value={filter} onChange={e => setFilter(e.target.value)} label="Status" sx={selectSx}>
            {[['all', 'All Listings'], ['approved', 'Approved'], ['pending', 'Pending Review'], ['rejected', 'Rejected']].map(([v, l]) => (
              <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{l}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={28} sx={{ color: 'var(--brand)' }} />
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-20" style={{ color: 'var(--danger)', fontFamily: 'DM Sans,sans-serif', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)', fontFamily: 'DM Sans,sans-serif', fontSize: 13.5 }}>
          No listings found.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <Grid container spacing={2.5}>
          {filtered.map(({ listing, providerName, providerMobile }) => {
            const status = (listing.approvalStatus || 'pending').toLowerCase();
            const sc = statusColors[status] || statusColors.pending;
            const charges = listing.additionalCharges;
            const extraChargesTotal =
              (charges?.wallPaintingCharge || 0) +
              (charges?.installationCharge || 0) +
              (charges?.removalCharge || 0) +
              (charges?.otherCharge || 0);

            return (
              <Grid item xs={12} md={6} xl={4} key={listing.id}>
                <div
                  className="bg-white rounded-[18px] overflow-hidden transition-all duration-200 flex flex-col h-full"
                  style={{ border: `1px solid ${sc.border}`, boxShadow: 'var(--shadow-sm)' }}
                >
                  {/* Image */}
                  <div className="relative h-[168px] overflow-hidden shrink-0">
                    {listing.photos && listing.photos.length > 0 ? (
                      <img src={listing.photos[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--bg-muted)' }}>
                        <span style={{ color: 'var(--text-muted)', fontFamily: 'DM Sans,sans-serif', fontSize: 12 }}>No photo</span>
                      </div>
                    )}
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 55%)' }} />
                    <div className="absolute top-2.5 left-2.5 flex gap-1.5">
                      {listing.photos && listing.photos.length > 1 && (
                        <span className="bg-black/55 backdrop-blur-sm text-[10px] font-semibold px-2 py-[3px] rounded-full text-white" style={{ fontFamily: 'DM Sans' }}>
                          +{listing.photos.length - 1} photos
                        </span>
                      )}
                    </div>
                    <div className="absolute top-2.5 right-2.5 flex gap-1.5">
                      <span className="bg-white/90 backdrop-blur-sm text-[10px] font-semibold px-2.5 py-[3px] rounded-full capitalize" style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans' }}>
                        {listing.mediaType.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <button
                      onClick={() => openDetail(listing.id)}
                      className="absolute bottom-2.5 right-2.5 flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-all duration-200 hover:brightness-95"
                      style={{ background: 'rgba(255,255,255,0.92)', border: 'none' }}
                      title="View details"
                    >
                      <Eye size={15} color="var(--brand)" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex flex-col flex-1">
                    <div className="font-semibold text-sm leading-snug mb-1" style={{ color: 'var(--text-primary)' }}>{listing.title}</div>

                    <a
                      href={googleMapsUrl(listing.latitude, listing.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1 text-xs mb-1 hover:underline w-fit"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <MapPin size={11} color="var(--brand)" />
                      {listing.area}
                      {listing.landmark ? `, ${listing.landmark}` : ''}, {listing.city}, {listing.state}
                      {listing.pinCode ? ` – ${listing.pinCode}` : ''}
                    </a>

                    <div className="text-xs mb-3" style={{ color: 'var(--text-muted)', fontFamily: 'DM Sans,sans-serif' }}>
                      {listing.description}
                    </div>

                    {/* full data grid */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mb-3 p-3 rounded-xl" style={{ background: 'var(--bg-muted)' }}>
                      <DetailRow icon={User} label="Provider" value={providerName} />
                      <DetailRow icon={Phone} label="Mobile" value={providerMobile} />
                      <DetailRow icon={Ruler} label="Dimensions" value={`${listing.width} × ${listing.height} ft`} />
                      <DetailRow icon={Navigation} label="Coordinates" value={`${listing.latitude}, ${listing.longitude}`} />
                      <DetailRow icon={Calendar} label="Available from" value={formatDate(listing.availableFrom)} />
                      <DetailRow icon={Calendar} label="Available to" value={formatDate(listing.availableTo)} />
                    </div>

                    {extraChargesTotal > 0 && charges && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {charges.wallPaintingCharge > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontFamily: 'DM Sans' }}>
                            <Tag size={10} /> Wall painting {formatCurrency(charges.wallPaintingCharge)}
                          </span>
                        )}
                        {charges.installationCharge > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontFamily: 'DM Sans' }}>
                            <Tag size={10} /> Installation {formatCurrency(charges.installationCharge)}
                          </span>
                        )}
                        {charges.removalCharge > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontFamily: 'DM Sans' }}>
                            <Tag size={10} /> Removal {formatCurrency(charges.removalCharge)}
                          </span>
                        )}
                        {charges.otherCharge > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontFamily: 'DM Sans' }}>
                            <Tag size={10} /> {charges.otherChargeDescription || 'Other'} {formatCurrency(charges.otherCharge)}
                          </span>
                        )}
                      </div>
                    )}

                    {listing.adminNote && (
                      <div className="flex items-start gap-1.5 mb-3 p-2.5 rounded-lg" style={{ background: 'var(--warning-bg)', border: '1px solid #f3dfa8' }}>
                        <StickyNote size={12} className="mt-[2px] shrink-0" color="var(--warning)" />
                        <span className="text-[11.5px]" style={{ color: 'var(--warning)', fontFamily: 'DM Sans' }}>{listing.adminNote}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center mb-3 mt-auto">
                      <span className="text-[11px] px-2 py-0.5 rounded-md flex items-center gap-1" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontFamily: 'DM Sans' }}>
                        <FileText size={10} /> S.No {listing.sno}
                      </span>
                      <span className="text-base font-bold flex items-center gap-0.5" style={{ color: 'var(--brand)', fontFamily: 'Playfair Display, serif' }}>
                        <IndianRupee size={14} />{listing.expectedRent.toLocaleString('en-IN')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div
                        className="flex-1 text-center text-[13px] font-semibold rounded-[9px] py-2.5 capitalize"
                        style={{ color: sc.color, background: sc.bg, border: `1px solid ${sc.border}`, fontFamily: 'DM Sans' }}
                      >
                        {status}
                      </div>
                      <button
                        onClick={() => openDetail(listing.id)}
                        className="flex items-center justify-center gap-1.5 rounded-[9px] py-2.5 px-3 text-xs font-semibold cursor-pointer transition-all duration-200 hover:brightness-95"
                        style={{ background: 'var(--brand-light)', border: '1px solid var(--brand-mid)', color: 'var(--brand)', fontFamily: 'DM Sans' }}
                      >
                        <Eye size={13} /> View
                      </button>
                    </div>
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AdminListingDetailPopup
        open={detailOpen}
        listingId={selectedId}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default AdminListings;